/**
 * Composition root: build the context, start the loop, run the boot, hang
 * the harness seam.  Everything this file used to be lives in app/ now --
 * context.js (the world and its state), loop.js (the frame order, as data),
 * boot.js (the async load).  The order below is the old file's order:
 * the loop draws from the first frame, the boot fills the world in behind
 * the title card, and window.catGame is hung once the boot is underway.
 */
async function initialize() {
  // Dynamic imports also catch failures during context creation (for example
  // unavailable WebGL), before any of the game's input handlers exist.
  const { game } = await import("./app/context.js");
  const { start } = await import("./app/loop.js");
  const { boot } = await import("./app/boot.js");

  start();

  // A handle for driving the game from a test harness: pose the player, force a
  // render, read the pixels back.  Cheap to keep, and the only way to check the
  // thing actually renders without a human at the keyboard.
  window.catGame = { renderer: game.renderer, scene: game.scene, camera: game.camera,
                         player: game.player, cats: game.cats, input: game.input,
                         grid: game.grid, level: game.level, debug: game.debug,
                         daycycle: game.daycycle,
                         customizer: () => game.customizer,
                         net: () => game.net, happyHud: () => game.happyHud,
                         purr: () => game.purr,
                         butterflies: () => game.butterflies,
                         prey: () => game.prey,
                         setCam: (y, p) => { game.camYaw = y; game.camPitch = p; },
                         shot: () => {
                           game.renderer.render(game.scene, game.camera);
                           return game.renderer.domElement.toDataURL("image/jpeg", 0.86);
                         } };
  await boot();
}

initialize().catch((error) => {
  console.error("Cat World could not load", error);
  document.querySelectorAll("#overlay, #join").forEach(el => el.classList.add("hidden"));
  const loading = document.getElementById("loading") ?? document.createElement("div");
  loading.id = "loading";
  loading.classList.remove("hidden");
  loading.style.pointerEvents = "auto";
  loading.setAttribute("role", "alert");
  const title = document.createElement("strong");
  title.textContent = "Couldn't start Cat World";
  const message = document.createElement("p");
  message.id = "startup-error";
  message.textContent = /webgl|graphics context/i.test(String(error))
    ? "Your browser couldn't start 3D graphics. Enable graphics acceleration in your browser settings, then reload."
    : "The neighbourhood couldn't finish loading. Reload to try again.";
  const retry = document.createElement("button");
  retry.type = "button";
  retry.textContent = "Reload";
  retry.addEventListener("click", () => location.reload());
  loading.replaceChildren(title, message, retry);
  if (!loading.isConnected) document.body.appendChild(loading);
});
